import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useLanguage } from "../i18n/LanguageContext";
import Logo from "../components/Logo";
import usePageTitle from "../hooks/usePageTitle";
import axios from "axios";
import { MapPin, Percent, ChevronRight, Search, UtensilsCrossed, ShoppingBag, Sparkles, Map, ExternalLink, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getMapsUrl = (b) => {
  if (b?.latitude && b?.longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${b.latitude},${b.longitude}`;
  }
  const q = [b?.name, b?.address].filter(Boolean).join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

const BusinessDirectoryPage = () => {
  usePageTitle('Partner Businesses', 'Discover exclusive discounts at restaurants, shops and services across Costa del Sol');
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "all");
  const [selectedLocation, setSelectedLocation] = useState(searchParams.get("location") || "all");
  const [subLocations, setSubLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const categories = [
    { id: "all", name: t('directory.all'), icon: Sparkles },
    { id: "dining", name: t('directory.dining'), icon: UtensilsCrossed },
    { id: "shopping", name: t('directory.shopping'), icon: ShoppingBag },
    { id: "services", name: t('directory.services'), icon: Sparkles },
  ];

  const [territory, setTerritory] = useState(localStorage.getItem("advanta_territory") || "costa_del_sol");

  const territoryNames = { costa_del_sol: "Costa del Sol", tenerife: "Tenerife" };

  const switchTerritory = (tid) => {
    setTerritory(tid);
    localStorage.setItem("advanta_territory", tid);
  };

  useEffect(() => {
    const fetchSubLocations = async () => {
      try {
        const response = await axios.get(`${API}/sub-locations?territory_id=${territory}`);
        setSubLocations(response.data);
      } catch (error) {
        console.error("Failed to fetch sub-locations:", error);
      }
    };
    fetchSubLocations();
  }, [territory]);

  useEffect(() => {
    fetchBusinesses();
  }, [selectedCategory, territory]);

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ territory_id: territory });
      if (selectedCategory !== "all") params.set("category", selectedCategory);
      const response = await axios.get(`${API}/businesses?${params}`);
      setBusinesses(response.data);
    } catch (error) {
      console.error("Failed to fetch businesses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", category);
    }
    setSearchParams(searchParams);
  };

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
    if (location === "all") {
      searchParams.delete("location");
    } else {
      searchParams.set("location", location);
    }
    setSearchParams(searchParams);
  };

  const filteredBusinesses = businesses.filter(business => {
    const matchesSearch = business.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      business.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = selectedLocation === "all" || business.sub_location === selectedLocation;
    return matchesSearch && matchesLocation;
  });

  return (
    <div className="min-h-screen bg-[#F8F8F6]">
      {/* Navigation */}
      <nav className="bg-[#020617] border-b border-[#D4AF37]/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <Link to="/businesses/map">
              <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10">
                <Map className="w-4 h-4 mr-2" />
                {t('directory.map_view')}
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button className="btn-gold px-6 py-2 rounded" data-testid="dashboard-nav-btn">{t('nav.dashboard')}</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-[#020617] py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <span className="text-[#D4AF37] text-sm tracking-widest uppercase">{t('directory.title')}</span>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mt-4">
              {t('directory.explore')} <span className="italic text-[#D4AF37]">{territoryNames[territory]}</span>
            </h1>
            <p className="text-white/50 mt-4">{t('directory.discover_desc')}</p>
            {/* Territory Tabs */}
            <div className="flex items-center justify-center gap-2 mt-6">
              {Object.entries(territoryNames).map(([tid, name]) => (
                <button
                  key={tid}
                  onClick={() => switchTerritory(tid)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    territory === tid
                      ? 'bg-[#D4AF37] text-[#020617] shadow-lg shadow-[#D4AF37]/20'
                      : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="text"
              placeholder={t('directory.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 border border-white/10 text-white placeholder:text-white/40 focus:border-[#D4AF37] focus:outline-none transition-colors"
              data-testid="search-input"
            />
          </div>
        </div>
      </div>

      {/* Categories & Location Filter */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Categories */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 flex-1">
              {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap ${
                  selectedCategory === cat.id
                    ? "bg-[#020617] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`category-${cat.id}`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.name}
              </button>
            ))}
            </div>
            
            {/* Location Filter */}
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <Select value={selectedLocation} onValueChange={handleLocationChange}>
                <SelectTrigger className="w-[180px] bg-white border-gray-200">
                  <SelectValue placeholder={t('directory.all_locations')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('directory.all_locations')}</SelectItem>
                  {subLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Business List */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin mx-auto" />
          </div>
        ) : filteredBusinesses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('directory.no_partners')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredBusinesses.map((business) => (
              <Link
                key={business.id}
                to={`/businesses/${business.id}`}
                className="block group"
                data-testid={`business-card-${business.id}`}
              >
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 p-3 sm:p-4 rounded-2xl hover:bg-white hover:shadow-lg transition-all duration-300">
                  <div className="w-full h-44 sm:w-48 sm:h-36 rounded-xl overflow-hidden sm:flex-shrink-0 bg-gray-100">
                    {business.image_url ? (
                      <img
                        src={business.image_url}
                        alt={business.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <Sparkles className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 py-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-[#D4AF37] text-xs font-medium tracking-wider uppercase">
                          {business.category}
                        </span>
                        <h3 className="font-['Playfair_Display'] text-2xl text-[#020617] mt-1 group-hover:text-[#D4AF37] transition-colors">
                          {business.name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-[#047857]/10 text-[#047857] font-bold">
                        <Percent className="w-4 h-4" />
                        <span>{business.discount_level}%</span>
                      </div>
                    </div>
                    <p className="text-gray-500 mt-2 line-clamp-2">{business.description}</p>
                    <div className="flex items-center gap-2 mt-4 text-gray-400 text-sm flex-wrap">
                      <MapPin className="w-4 h-4" />
                      <span>{business.address}</span>
                      {business.address && (
                        <a
                          href={getMapsUrl(business)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-xs hover:bg-[#D4AF37]/20 transition-colors"
                          data-testid={`maps-link-${business.id}`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          Maps
                        </a>
                      )}
                    </div>
                    {business.phone && (
                      <div className="flex items-center gap-2 mt-2 text-gray-400 text-sm">
                        <Phone className="w-4 h-4" />
                        <a
                          href={`tel:${business.phone.replace(/\s+/g, "")}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-[#D4AF37] transition-colors"
                          data-testid={`phone-link-${business.id}`}
                        >
                          {business.phone}
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="hidden sm:flex items-center">
                    <ChevronRight className="w-6 h-6 text-gray-300 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessDirectoryPage;
